import { useElements, useStripe } from "@stripe/react-stripe-js";
import {
  PaymentMethodCreateParams,
  SetupIntentResult,
  StripeElements,
  StripeError,
} from "@stripe/stripe-js";
import { useMutation } from "@tanstack/react-query";

const PAYMENT_METHOD_SETUP_ERROR_MESSAGE = "An error occurred verifying this payment method.";

const createPaymentElementSetupError = (error?: Pick<StripeError, "message" | "decline_code">) => {
  return Object.assign(new Error(error?.message ?? PAYMENT_METHOD_SETUP_ERROR_MESSAGE), {
    decline_code: error?.decline_code,
  });
};

type PaymentElementSetupError = ReturnType<typeof createPaymentElementSetupError>;

type ConfirmPaymentElementSetupParams = {
  confirmSetup: () => Promise<SetupIntentResult>;
  submit: StripeElements["submit"];
};

export const confirmPaymentElementSetup = async ({
  confirmSetup,
  submit,
}: ConfirmPaymentElementSetupParams) => {
  const submitPayload = await submit();
  if (submitPayload.error) {
    throw createPaymentElementSetupError(submitPayload.error);
  }

  const payload = await confirmSetup();
  if (payload.error) {
    throw createPaymentElementSetupError(payload.error);
  }

  const paymentMethod = payload.setupIntent?.payment_method;
  if (!paymentMethod) {
    throw createPaymentElementSetupError();
  }

  return typeof paymentMethod === "string" ? paymentMethod : paymentMethod.id;
};

type ConfirmPaymentElementSetupVariables = {
  billingDetails: PaymentMethodCreateParams.BillingDetails;
};

export const useConfirmPaymentElementSetupMutation = () => {
  const stripe = useStripe();
  const elements = useElements();

  return useMutation<string, PaymentElementSetupError, ConfirmPaymentElementSetupVariables>(
    async ({ billingDetails }) => {
      if (!stripe || !elements) {
        throw createPaymentElementSetupError();
      }

      return confirmPaymentElementSetup({
        confirmSetup: () =>
          stripe.confirmSetup({
            elements,
            redirect: "if_required",
            confirmParams: {
              payment_method_data: { billing_details: billingDetails },
              return_url: window.location.href,
            },
          }),
        submit: () => elements.submit(),
      });
    },
  );
};
